import { ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import { HttpService, } from '@philips-internal/oswald-ui-core/lib/services';
import { HttpResponse } from '@philips-internal/oswald-ui-core/lib/services';
import { MedicationViewerParams } from '../eletronic-prescription/medication-viewer/medication-viewer-params';

import { Endpoint } from './../../../constants/endpoints';
import { BodyCheckInfo, FirstMedicationCheckInfoResponse } from '../eletronic-prescription/medication-viewer/medication-viewer-types';
import { FoundationUtils } from './../../../utils/foundation-utils';
import { ADEP_E1113Parameters, FunctionName } from './../../../utils/parameters';
import { Params } from '../eletronic-prescription/administration-schedule/administration-schedule-types';
import { PrescriptionType } from './timeline-service';

export interface ConsistAdministrationMarginInfoRequest {
  actionAdministrationOutsideMarginStatus: string;
  eventDate: string;
  hasAsEvent: string;
  itemType: string;
  justificationDescription: string;
  minutesMarginBefore: number;
  minutesQuantityMarginAdministration: number;
  requiresJustificationStatus: string;
  scheduleSequence: number;
}

export interface ConsistAdministrationMarginInfoResponse {
  responseDescription: string;
}

export interface AllowedAdministerItemParams {
  internalProcessSequence?: number;
  isPalm?: string;
  itemCode?: number;
  itemType?: string;
  originProcedureType?: number;
  profileCode?: number;
  sectorCode?: number;
  userName?: string;
}

export interface CreateFirstMedicationCheckRequest {
  encounterNumber: number;
  eventDate: string;
  itemType: string;
  justificationDescription: string;
  scheduleSequence: number;
}

export interface CreateFirstMedicationCheckResponse {
  created: string;
}

export interface CreatePrescriptionScheduleChangeRequest {
  administrationRouteType: string;
  changeType?: number;
  componentsDescription: string;
  encounterNumber: number;
  establishmentCode?: number;
  eventDate: string;
  functionCode: number;
  glucoseProteinSequence?: number;
  groupingNumber?: number;
  intervalCode: string;
  isGenerateAll: string;
  isPalm?: 'N' | 'S';
  itemCode: string;
  itemDescription: string;
  itemObservationDescription: string;
  itemQuantity: number;
  itemSequence?: number;
  itemType: string;
  justifyDescription: string;
  messageDescriptionResponse: string;
  observationDescription: string;
  pdoPrn: string;
  prescriptionHourQuantity: number;
  prescriptionNumber: number;
  prescriptionsNumber: string;
  procedureInternalSequenceNumber?: number;
  profileCode?: number;
  quantityAdministered?: number;
  scheduleSequence: number;
  suspendPrescribedMaterial: string;
  unitMeasureCode: string;
  uoMCodeQuantityAdministered: string;
  userName?: string;
}

export interface CreatePrescriptionScheduleChangeResponse {
  evolutionCodeResponse: number;
  messageDescriptionResponse: string;
}

export interface CreateEventMedicationConferenceRequest {
  changeStatus: number;
  encounterNumber: number;
  glucoseProteinSequence: number;
  internalProcessSequence: number;
  pdoPrn: string;
  itemCode: number;
  itemSequence: number;
  itemType: string;
  justifyDescription: string;
  prescriptionNumner: number;
  scheduleSequence: number;
  userName?: string;
  changeDate: string;
}

export interface ConvertBarCodeRequest {
  barCodeType: string;
  localStockCode: string;
  materialBarCode: string;
  requisitionNumber: string;
}

export interface ConvertBarCodeResponse {
  batchGroupSequenceResponse: number;
  batchSequenceResponse: number;
  errorDescriptionResponse: string;
  lpTagNumberResponse: string;
  materialCodeResponse: number;
  materialDescriptionResponse: string;
  materialKitCodeResponse: number;
  materialQuantityResponse: number;
  unitMeasureCodeResponse: string;
  validateDescriptionResponse: string;
}

export interface ScheduleSuspendedResponse {
  materialSuspendedDate: string;
  medicalSuspendedDate: string;
}

export interface AdministrationChemotherapyMedicationRequest {
  administeredStatus: string;
  barcode: string;
  encounterIdSequence: number;
  groupingSequence: number;
  materialSequence: number;
  orderSequence: number;
  prescriptionNumber: number;
}

export interface AdministrationChemotherapyMedicationResponse {
  message?: string;
}

export default class MedicationService {

  public static showToast(message: string, typeMessage: ToastLevel): void {
    const toastTime = 5000;
    const toastId = 'toast-service';

    ToastService.close(toastId);
    ToastService.open({
      dismissExpression: message,
      dismissible: true,
      highlight: false,
      id: toastId,
      level: typeMessage,
      message: message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: toastTime,
      type: ToastType.BANNER,
    });
  }

  public static doesAllowAdministerItem(params: AllowedAdministerItemParams): Promise<boolean> {
    const body = Object.assign({}, params) as unknown as Record<string, Value>;

    return HttpService.post(
      Endpoint.EMAR_GET_ALLOW_ADMINISTER_ITEM, {},
      { body }
    ).then(response => {
      return response.body as boolean;
    });
  }

  public static consistAdministrationMarginInfoResponse(
    request: ConsistAdministrationMarginInfoRequest): Promise<ConsistAdministrationMarginInfoResponse> {

    const body = Object.assign({}, request) as unknown as Record<string, Value>;

    return HttpService.post(
      Endpoint.EMAR_CONSIST_ADMINISTRATION_MARGIN_INFO, {},
      { body }
    ).then(response => {
      return response.body as ConsistAdministrationMarginInfoResponse;
    });
  }

  public static consistProcessSchedule(scheduleSequence: number): Promise<HttpResponse<unknown>> {
    return HttpService.post(Endpoint.EMAR_CONSIST_PROCESS_SCHEDULE, { scheduleSequence });
  }

  public static createFirstMedicationCheck(request: CreateFirstMedicationCheckRequest): Promise<CreateFirstMedicationCheckResponse> {
    const body = Object.assign({}, request) as unknown as Record<string, Value>;

    return HttpService.post(
      Endpoint.EMAR_CREATE_FIRST_MEDICATION_CHECK, {},
      { body }
    ).then(response => {
      return response.body as CreateFirstMedicationCheckResponse;
    });
  }

  public static createPrescriptionScheduleChange(request: CreatePrescriptionScheduleChangeRequest): Promise<CreatePrescriptionScheduleChangeResponse> {
    const body = Object.assign({}, request) as unknown as Record<string, Value>;

    return HttpService.post(
      Endpoint.EMAR_CREATE_PRESCRIPTION_SCHEDULE_CHANGE, {},
      { body }
    ).then(response => {
      return response.body as CreatePrescriptionScheduleChangeResponse;
    });
  }

  public static administrationChemotherapyMedication(request: MedicationViewerParams): Promise<AdministrationChemotherapyMedicationResponse> {
    const body = Object.assign({}, request) as unknown as Record<string, Value>;

    return HttpService.post(
      Endpoint.EMAR_CHEMOTHERAPY_ADMINISTRATION, {},
      { body }
    ).then(response => {
      return response.body as AdministrationChemotherapyMedicationResponse;
    });
  }

  public static validateChemotherapyAdministration(request: AdministrationChemotherapyMedicationRequest): Promise<AdministrationChemotherapyMedicationResponse> {
    const body = Object.assign({}, request) as unknown as Record<string, Value>;

    return HttpService.post(
      Endpoint.EMAR_VALIDATE_CHEMOTHERAPY_ADMINISTRATION, {},
      { body }
    ).then(response => {
      return response.body as AdministrationChemotherapyMedicationResponse;
    });
  }

  public static createEventMedicationConference(request: CreateEventMedicationConferenceRequest): Promise<HttpResponse<unknown>> {
    const body = Object.assign({}, request) as unknown as Record<string, Value>;

    return HttpService.post(
      Endpoint.EMAR_CREATE_EVENT_MEDICATION_CONFERENCE, {}, { body }
    );
  }

  public static convertBarCode(request: ConvertBarCodeRequest): Promise<ConvertBarCodeResponse> {
    const body = Object.assign({}, request) as unknown as Record<string, Value>;

    return HttpService.post(
      Endpoint.EMAR_CONVERT_BAR_CODE, {},
      { body }
    ).then(response => {
      return response.body as ConvertBarCodeResponse;
    });
  }

  public static findBatchMaterialSupplied(materialCode: number, barsDescription: string): Promise<number> {
    return HttpService.post(
      Endpoint.EMAR_GET_BATCH_MATERIAL_SUPPLIED, {
        barsDescription,
        materialCode,
      }
    ).then(response => {
      return response.body as number;
    });
  }

  public static findScheduleSuspended(scheduleMaterialSequence: number): Promise<ScheduleSuspendedResponse> {
    return HttpService.get(
      Endpoint.EMAR_FIND_SCHEDULE_SUSPENDED, { scheduleMaterialSequence }
    ).then(response => {
      return response.body as ScheduleSuspendedResponse;
    });
  }

  public static async validateStatusItem(bodyCheckInfo: BodyCheckInfo): Promise<boolean> {
    const responseFirstMedication = await this.statusMedicationCheck(bodyCheckInfo, false);
    return responseFirstMedication?.responseDescription ? true : false;
  }

  public static async statusMedicationCheck(bodyCheckInfo: BodyCheckInfo, ignoreParam: boolean): Promise<FirstMedicationCheckInfoResponse | null> {
    const param561 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOWS_YOU_TO_PERFORM_THE_FIRST_MEDICATION_CHECK);
    const canRealizeDoubleCheck = ignoreParam || param561 === Params.ALLOWED;
    const itemType = [PrescriptionType.MEDICINE, PrescriptionType.GLYCEMIA_CONTROLS_ASSOCIATED_ITEMS,
      PrescriptionType.PROCEDURES_ASSOCIATED_ITEMS, PrescriptionType.BLOOD_MANAGEMENT] as string[];

    if (itemType.includes(String(bodyCheckInfo.itemType)) && canRealizeDoubleCheck) {
      const body = bodyCheckInfo as unknown as Record<string, Value>;
      return await HttpService.post(Endpoint.EMAR_GET_INFO_FIRST_MEDICATION_CHECK, {}, { body })
        .then(response => {
          const responseBody = response.body as FirstMedicationCheckInfoResponse;
          return responseBody;
        });
    }
    return Promise.resolve(null);
  }
}
